import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Sprout, Loader2 } from "lucide-react";

export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password.length < 6) {
      setErr("A senha precisa ter pelo menos 6 caracteres.");
      return;
    }
    setLoading(true);
    try {
      await register(form.email, form.password, form.name);
      nav("/dashboard", { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div
        className="hidden lg:flex relative flex-col justify-end p-12 text-white overflow-hidden grain-overlay"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(27,67,50,0.55), rgba(17,28,21,0.9)), url('https://images.pexels.com/photos/4975384/pexels-photo-4975384.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=940')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10 max-w-md">
          <div className="flex items-center gap-2 mb-6">
            <span className="h-10 w-10 rounded-md bg-accent text-accent-foreground flex items-center justify-center">
              <Sprout className="h-6 w-6" />
            </span>
            <div className="font-display font-bold text-xl">PRONAF Projects</div>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight">
            Tempo economizado,
            <br /> <span className="text-accent">produtores melhor atendidos.</span>
          </h1>
          <p className="mt-4 text-white/80 text-sm">
            Cadastre-se para acessar o formulário guiado de projetos simplificados PRONAF,
            cálculos automáticos e geração de PDFs profissionais.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 bg-background">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="register-form">
          <div>
            <h2 className="font-display text-3xl font-bold">Criar conta</h2>
            <p className="text-sm text-muted-foreground mt-1">Comece a gerar projetos em minutos.</p>
          </div>
          {err && (
            <Alert variant="destructive" data-testid="register-error">
              <AlertDescription>{err}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <Label htmlFor="name">Nome completo</Label>
            <Input
              id="name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="register-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">E-mail</Label>
            <Input
              id="email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="register-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Senha</Label>
            <Input
              id="password"
              type="password"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="register-password-input"
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-button">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Criar conta
          </Button>
          <p className="text-sm text-muted-foreground text-center">
            Já tem conta?{" "}
            <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="go-login-link">
              Entrar
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
